import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { EsignService } from '../../services/esign-service/esign.service';
import { EsignOverlayService } from '../../services/esign-overlay/esign-overlay.service';
import { StorageKeys } from '../../shared/enums/storage.enum';
import { StorageService } from '../../services/storage/storage.service';

interface ActivityItem {
  icon: 'esign' | 'send' | 'recharge';
  title: string;
  date: string;
  credits: number;
}

interface TransactionItem {
  id: string;
  date: string;
  type: 'Credit' | 'Debit';
  credits: number;
  formattedCredits: string;
  amount: number;
  description: string;
  paymentId: string;
  remaingCredits: number;
}

@Component({
  selector: 'app-esign-dashboard',
  templateUrl: './esign-dashboard.component.html',
  styleUrl: './esign-dashboard.component.scss',
  standalone: false
})
export class EsignDashboardComponent implements OnInit, OnDestroy {
  userName = '';
  documentsSignedCount = 0;
  creditsLeft = 0;
  awaitingOthersCount = 0;
  walletCredits = 0;
  recentActivities: ActivityItem[] = [];
  showAddCreditsModal = false;
  private creditsSub!: Subscription;
  transactions: TransactionItem[] = [];
  transactionCount = 0;
  transactionPage = 0;
  readonly PAGE_SIZE = 10;
  showAllActivity = false;
  loadingTransactions = false;
  transactionErrorMessage = '';

  get walletDescription(): string {
    return `Credits = ${this.walletCredits} document${this.walletCredits !== 1 ? 's' : ''}`;
  }
  constructor(
    private readonly esignOverlayService: EsignOverlayService,
    private readonly esignService: EsignService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly storageService: StorageService,
  ) {}

  ngOnInit(): void {
    const cached = this.storageService.getDataFromLocalStorage(StorageKeys.SELF_DETAILS);
    if (cached?.name) {
      this.userName = this.extractDisplayName(cached.name);
    }

    this.route.queryParams.subscribe((params) => {
      if (params['token']) {
        try {
          const decoded = JSON.parse(atob(params['token']));
          this.storageService.setDataToLocalStorage(StorageKeys.ACCESS_TOKEN, decoded.token);
        } catch (e) {
          console.error('Invalid token received.');
        }
      }
      this.getSelfDetails();
      this.fetchCredits();
      this.fetchStats();
      this.fetchTransactionHistory();
      this.transactionCount = 0;
      this.fetchTransactionCount();
      this.location.replaceState(this.router.url?.split('?')[0]);
    });

    this.creditsSub = this.authService.credits$.subscribe(val => {
      this.creditsLeft = val;
      this.walletCredits = val;
    });
  }

  ngOnDestroy(): void {
    this.creditsSub?.unsubscribe();
  }

  openEsignOverlay(): void {
    this.esignOverlayService.open();
  }

  fetchCredits(): void {
    this.authService.triggerCreditsRefresh();
  }

  fetchStats(): void {
    this.esignService.getStats().subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (data) {
          this.documentsSignedCount = data.totalSigned      ?? this.documentsSignedCount;
          this.creditsLeft          = data.availableCredits ?? this.creditsLeft;
          this.walletCredits        = data.availableCredits ?? this.walletCredits;
          this.awaitingOthersCount  = data.pendingDocuments ?? this.awaitingOthersCount;
          this.authService.setCredits(data.availableCredits ?? this.creditsLeft);
        }
      },
      error: () => { /* silent */ },
    });
  }

  getSelfDetails(): void {
    this.authService.getSelfDetails().subscribe({
      next: (res: any) => {
        if (res?.data) {
          this.storageService.setDataToLocalStorage(StorageKeys.SELF_DETAILS, res.data);
          this.authService.setUser(res.data);
          this.userName = this.extractDisplayName(res.data.name);
        }
      },
      error: (err: any) => {
        console.error('Failed to fetch self details:', err);
      }
    });
  }

  private extractDisplayName(raw: string): string {
    if (!raw) return '';
    const local = raw.includes('@') ? raw.split('@')[0] : raw;
    const words = local
      .split(/[._\-0-9]+/)
      .map(w => w.trim())
      .filter(w => w.length > 0)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    return words.join(' ') || raw;
  }

  fetchTransactionHistory(page: number = 0): void {
    this.loadingTransactions = true;
    this.transactionErrorMessage = '';
    this.esignService.getTransactionHistory(page).subscribe({
      next: (res: any) => {
        this.transactions = res?.data ?? [];
        this.loadingTransactions = false;
        this.transactionErrorMessage = '';
      },
      error: (error: any) => {
        this.loadingTransactions = false;
        this.transactionErrorMessage = error?.error?.error?.message || 'Failed to load recent activity.';
      },
    });
  }

  fetchTransactionCount(): void {
    this.esignService.getTransactionHistoryCount().subscribe({
      next: (res: any) => { this.transactionCount = res?.data ?? 0; },
      error: () => { /* silent */ },
    });
  }

  onTransactionPageChange(event: any): void {
    this.transactionPage = event.page;
    this.fetchTransactionHistory(event.page);
  }

  toggleAllActivity(): void {
    this.showAllActivity = !this.showAllActivity;
  }

  getActivityIcon(tx: TransactionItem): 'esign' | 'send' | 'recharge' {
    if (tx.type === 'Credit') return 'recharge';
    if (tx.description?.toLowerCase().includes('shared')) return 'send';
    return 'esign';
  }

  openRecharge(): void {
    this.showAddCreditsModal = true;
  }

  onModalClose(): void {
    this.showAddCreditsModal = false;
    this.authService.triggerCreditsRefresh();
  }
}
